import React, { useState } from "react";
import CustomModal from "../CustomModal/CustomModal";
import DropOffProcedure from "./DropOffProcedure";
import NavLinks from "./NavLinks";
import Overview from "./Overview";
import PickUpProcedure from "./PickUpProcedure";
import Reviews from "./Reviews";
import TermsCondition from "./TermsCondition";

//import react icons
import { FaChevronRight } from "react-icons/fa";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { BsStarFill } from "react-icons/bs";

const contents = {
  overview: (product) => <Overview product={product} />,
  dropoffprocedure: (product) => <DropOffProcedure product={product} />,
  pickupprocedure: (product) => <PickUpProcedure product={product} />,
  reviews: (product) => <Reviews product={product} />,
  termscondition: (product) => <TermsCondition product={product} />,
};

const MoreInfo = ({ show, setShow, product }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;
  const getDeparture = params.get("departure");
  const getArrival = params.get("arrival");
  const getPromocode = params.get("promocode");
  const getAirport = params.get("airport");

  const [clicked, setClicked] = useState(false);
  const [content, setContent] = useState("overview");

  if (!product) return null;

  const rating = product?.average_rating ? parseFloat(product.average_rating) : 0;

  const handleBookNow = () => {
    setClicked(true);
    navigate(
      `/booking?${new URLSearchParams({
        departure: getDeparture,
        arrival: getArrival,
        promocode: getPromocode,
        airport: getAirport,
        product: product?.sku?.toString(),
      })}`
    );
  };

  return (
    <CustomModal isOpen={show} onClose={() => setShow(false)}>
      <div className="flex flex-col h-full bg-slate-50">
        {/* Sticky Header Section - Improved Colors */}
        <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-100 px-4 py-3 sm:px-10 sm:py-6 shadow-sm">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3 sm:gap-6">
            <div className="flex items-center gap-4 text-left w-full lg:w-auto">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-3xl lg:text-4xl font-[900] text-primary tracking-tight leading-tight mb-1 truncate">
                  {product?.name}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center bg-yellow/10 px-2 py-0.5 rounded-md border border-yellow/20">
                    {[...Array(5)].map((_, index) => (
                      <BsStarFill
                        key={index}
                        className={`text-[8px] sm:text-xs ${index < Math.floor(rating) ? "text-yellow" : "text-slate-300"
                          } mr-0.5`}
                      />
                    ))}
                    <span className="ml-1.5 text-[8px] sm:text-[10px] font-black text-yellow-700">{rating}</span>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] sm:text-xs font-bold bg-primary/5 text-primary border border-primary/10 uppercase tracking-widest">
                    {product?.type || "Parking"}
                  </span>
                  {product?.offer && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[8px] sm:text-[10px] font-black bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-100 animate-pulse">
                      {product?.offer}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between w-full lg:w-auto lg:justify-end gap-3 sm:gap-6 bg-slate-50/50 p-2 sm:p-5 rounded-2xl border border-slate-100 lg:bg-transparent lg:p-0 lg:border-0">
              <div className="text-left lg:text-right">
                <p className="text-slate-500 text-[8px] sm:text-[10px] uppercase font-black tracking-widest leading-none mb-1">Total Price</p>
                <div className="flex items-baseline lg:justify-end gap-0.5 text-primary">
                  <span className="text-xs sm:text-xl font-black leading-none self-start mt-0.5">£</span>
                  <h4 className="text-xl sm:text-4xl font-[900] tracking-tighter leading-none">
                    {product?.price}
                  </h4>
                </div>
              </div>
              <button
                className={`group relative overflow-hidden bg-primary text-white px-5 sm:px-10 py-3 sm:py-5 rounded-xl sm:rounded-2xl font-black text-[12px] sm:text-lg transition-all active:scale-95 small:mr-10 disabled:opacity-70 ${pathname === "/booking" ? "hidden" : "flex"} items-center gap-2 shadow-xl shadow-primary/20 hover:shadow-primary/40`}
                onClick={handleBookNow}
                disabled={clicked}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#2c5b96] to-primary translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <span className="relative z-10">{clicked ? "Processing..." : "Reserve Now"}</span>
                <FaChevronRight className="relative z-10 text-[8px] sm:text-base group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Content Section with Sidebar */}
        <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Sidebar / Tabs */}
          <div className="lg:w-80 w-full bg-white lg:border-r border-slate-100 p-3 sm:p-6 lg:overflow-y-auto no-scrollbar">
            <NavLinks content={content} setContent={setContent} />
          </div>

          {/* Scrolling Content Area */}
          <div className="flex-1 overflow-y-auto custom-scroll p-2 sm:p-4 bg-slate-50/50">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-[1rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden transform transition-all duration-500">
                <div className="p-4 text-slate-600 leading-relaxed overflow-hidden break-words">
                  <div className="custom-html-content">
                    {contents[content](product)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default MoreInfo;
